import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import QRCode from 'qrcode';
import './QRManagement.css';

const QRManagement = () => {
  const user = useSelector(state => state.AuthReducer?.user);
  const isAdmin = user?.role === 'admin';
  const isAdminOrUserAdmin = user?.role === 'admin' || user?.role === 'useradmin';

  // Onglet actif : par défaut 'reclamations' pour les caissiers/utilisateurs, 'qrcodes' pour l'admin
  const [activeTab, setActiveTab] = useState(isAdminOrUserAdmin ? 'qrcodes' : 'reclamations'); // 'qrcodes' | 'categories' | 'reclamations' | 'stats'

  // Data states
  const [locations, setLocations] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [reclamations, setReclamations] = useState([]);
  const [stats, setStats] = useState(null);

  // Motifs de pannes / Catégories par localisation
  const [selectedLocForCat, setSelectedLocForCat] = useState('');
  const [locationCategories, setLocationCategories] = useState([]);
  const [newCatName, setNewCatName] = useState('');
  const [newCatType, setNewCatType] = useState('reclamation');
  const [loadingCats, setLoadingCats] = useState(false);

  // Filter states
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('');

  // Form states - Nouvel Équipement
  const [newLocationName, setNewLocationName] = useState('');
  const [selectedLocationForEquip, setSelectedLocationForEquip] = useState('');
  const [newEquipName, setNewEquipName] = useState('');
  const [newEquipCategory, setNewEquipCategory] = useState('Chaise de massage');

  // Photo & Video Modals
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null); // { url, id, clientName, equipmentName, locationName, duration, size }

  // QR Poster Modal
  const [posterEquip, setPosterEquip] = useState(null);
  const [posterQrUrl, setPosterQrUrl] = useState('');

  // Loading states
  const [submittingLoc, setSubmittingLoc] = useState(false);
  const [submittingEquip, setSubmittingEquip] = useState(false);
  const [submittingCat, setSubmittingCat] = useState(false);
  const [deletingVideoId, setDeletingVideoId] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorised: token } };
  };

  useEffect(() => {
    fetchLocations();
    fetchEquipments();
    fetchReclamations();
    fetchStats();
  }, []);

  useEffect(() => {
    if (selectedLocForCat) {
      fetchLocationCategories(selectedLocForCat);
    }
  }, [selectedLocForCat]);

  const fetchLocations = async () => {
    try {
      const res = await axios.get('/api/Locations/getAllLocations', getAuthHeaders());
      const locList = Array.isArray(res.data) ? res.data : (res.data.locations || []);
      setLocations(locList);
      if (locList.length > 0 && !selectedLocForCat) {
        setSelectedLocForCat(locList[0]._id);
      }
    } catch (err) {
      console.error('Erreur chargement localisations:', err);
    }
  };

  const fetchEquipments = async () => {
    try {
      const res = await axios.get('/api/qr-equipments', getAuthHeaders());
      if (res.data.success) {
        setEquipments(res.data.equipments);
      }
    } catch (err) {
      console.error('Erreur chargement équipements:', err);
    }
  };

  const fetchReclamations = async () => {
    try {
      const res = await axios.get('/api/reclamations', getAuthHeaders());
      if (res.data.success) {
        setReclamations(res.data.reclamations);
      }
    } catch (err) {
      console.error('Erreur chargement réclamations:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/reclamations/stats', getAuthHeaders());
      if (res.data.success) {
        setStats(res.data.stats);
      }
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  };

  const fetchLocationCategories = async (locationId) => {
    if (!locationId) return;
    try {
      setLoadingCats(true);
      const res = await axios.get(`/api/qr-categories/location/${locationId}`);
      if (res.data.success) {
        setLocationCategories(res.data.categories);
      }
    } catch (err) {
      console.error('Erreur chargement motifs:', err);
    } finally {
      setLoadingCats(false);
    }
  };

  // Créer une nouvelle localisation
  const handleCreateLocation = async (e) => {
    e.preventDefault();
    if (!newLocationName.trim()) return;
    try {
      setSubmittingLoc(true);
      await axios.post('/api/Locations/addLocation', { name: newLocationName.trim() }, getAuthHeaders());
      setNewLocationName('');
      fetchLocations();
      alert('Localisation créée avec succès !');
    } catch (err) {
      console.error('Erreur création localisation:', err);
      alert(err.response?.data?.message || 'Erreur lors de la création de la localisation.');
    } finally {
      setSubmittingLoc(false);
    }
  };

  // Créer un nouvel équipement / spot QR
  const handleCreateEquipment = async (e) => {
    e.preventDefault();
    if (!selectedLocationForEquip || !newEquipName.trim()) {
      alert('Veuillez sélectionner une localisation et donner un nom à l\'équipement.');
      return;
    }

    try {
      setSubmittingEquip(true);
      const res = await axios.post('/api/qr-equipments', {
        location: selectedLocationForEquip,
        name: newEquipName.trim(),
        category: newEquipCategory
      }, getAuthHeaders());

      if (res.data.success) {
        setNewEquipName('');
        fetchEquipments();
        alert(`Équipement "${res.data.equipment.name}" ajouté avec son QR code !`);
      }
    } catch (err) {
      console.error('Erreur création équipement:', err);
      alert(err.response?.data?.message || 'Erreur lors de l\'ajout de l\'équipement.');
    } finally {
      setSubmittingEquip(false);
    }
  };

  // Ajouter un motif / type de panne personnalisé pour une localisation
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!selectedLocForCat || !newCatName.trim()) {
      alert('Veuillez sélectionner une localisation et saisir le nom du motif.');
      return;
    }

    try {
      setSubmittingCat(true);
      const res = await axios.post('/api/qr-categories', {
        location: selectedLocForCat,
        name: newCatName.trim(),
        type: newCatType
      });

      if (res.data.success) {
        setNewCatName('');
        fetchLocationCategories(selectedLocForCat);
        alert('Motif de panne ajouté !');
      }
    } catch (err) {
      console.error('Erreur création motif:', err);
      alert('Erreur lors de l\'ajout du motif.');
    } finally {
      setSubmittingCat(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Voulez-vous supprimer ce motif de panne de la liste ?')) return;
    try {
      const res = await axios.delete(`/api/qr-categories/${id}`);
      if (res.data.success) {
        fetchLocationCategories(selectedLocForCat);
      }
    } catch (err) {
      alert('Erreur lors de la suppression.');
    }
  };

  const handleDeleteEquipment = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cet équipement QR ?')) return;
    try {
      const res = await axios.delete(`/api/qr-equipments/${id}`, getAuthHeaders());
      if (res.data.success) {
        fetchEquipments();
      }
    } catch (err) {
      alert('Erreur lors de la suppression de l\'équipement.');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const res = await axios.put(`/api/reclamations/${id}/status`, { status: newStatus }, getAuthHeaders());
      if (res.data.success) {
        fetchReclamations();
        fetchStats();
      }
    } catch (err) {
      alert('Erreur lors du changement de statut.');
    }
  };

  // Suppression définitive de la vidéo exclusivement par l'Admin
  const handleDeleteVideo = async (reclamationId) => {
    if (!isAdmin) {
      alert('Seul l\'administrateur est autorisé à supprimer définitivement une vidéo.');
      return;
    }

    if (!window.confirm('⚠️ ATTENTION : Voulez-vous supprimer DÉFINITIVEMENT cette vidéo du serveur et de la base de données ? Cette action est irréversible.')) {
      return;
    }

    try {
      setDeletingVideoId(reclamationId);
      const res = await axios.delete(`/api/reclamations/${reclamationId}/video`, getAuthHeaders());
      if (res.data.success) {
        alert('✅ Vidéo supprimée définitivement.');
        setSelectedVideo(null);
        fetchReclamations();
        fetchStats();
      }
    } catch (err) {
      console.error('Erreur suppression vidéo:', err);
      alert(err.response?.data?.message || 'Erreur lors de la suppression de la vidéo.');
    } finally {
      setDeletingVideoId(null);
    }
  };

  // Suppression d'une réclamation complète (Admin uniquement)
  const handleDeleteReclamation = async (id) => {
    if (!isAdmin) {
      alert('Seul l\'administrateur est autorisé à supprimer une réclamation.');
      return;
    }

    if (!window.confirm('Voulez-vous supprimer définitivement cette réclamation et tous ses fichiers ?')) return;
    try {
      const res = await axios.delete(`/api/reclamations/${id}`, getAuthHeaders());
      if (res.data.success) {
        fetchReclamations();
        fetchStats();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la suppression.');
    }
  };

  const getClientFormUrl = (qrToken) => {
    const origin = window.location.origin;
    return `${origin}/reclamation/${qrToken}`;
  };

  const handleOpenPoster = async (equip) => {
    const url = getClientFormUrl(equip.qrToken);
    try {
      const qrData = await QRCode.toDataURL(url, {
        width: 320,
        margin: 2,
        color: { dark: '#0f172a', light: '#ffffff' }
      });
      setPosterQrUrl(qrData);
      setPosterEquip(equip);
    } catch (err) {
      console.error('Erreur génération QR poster:', err);
    }
  };

  const handleCopyUrl = (qrToken) => {
    const url = getClientFormUrl(qrToken);
    navigator.clipboard.writeText(url);
    alert('Lien direct copié dans le presse-papier !');
  };

  const handlePrintPoster = () => {
    window.print();
  };

  // Télécharger UNIQUEMENT le QR Code (image PNG haute définition sans l'affiche)
  const handleDownloadOnlyQRCode = async (equip) => {
    if (!equip) return;
    const url = getClientFormUrl(equip.qrToken);
    try {
      const qrDataUrl = await QRCode.toDataURL(url, {
        width: 1024,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        },
        errorCorrectionLevel: 'H'
      });

      const locName = (equip.location?.name || 'Site').replace(/[^a-zA-Z0-9_-]/g, '_');
      const equipName = (equip.name || 'Equipement').replace(/[^a-zA-Z0-9_-]/g, '_');
      const filename = `QR_${locName}_${equipName}.png`;

      const link = document.createElement('a');
      link.href = qrDataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Erreur téléchargement QR code seul:', err);
      alert('Erreur lors du téléchargement du QR code.');
    }
  };

  // Filtrage des réclamations
  const filteredReclamations = reclamations.filter(rec => {
    if (selectedLocationFilter && rec.location?._id !== selectedLocationFilter) return false;
    if (selectedStatusFilter && rec.status !== selectedStatusFilter) return false;
    if (selectedTypeFilter && rec.type !== selectedTypeFilter) return false;
    return true;
  });

  const pendingCount = reclamations.filter(r => r.status === 'en_attente').length;

  return (
    <div className="qr-admin-container">
      {/* ================= EN-TÊTE DE LA PAGE ================= */}
      <div className="qr-admin-header-card">
        <div className="qr-title-box">
          <h2>📱 Gestion QR & Réclamations Clients</h2>
          <p className="qr-subtitle">
            {isAdminOrUserAdmin 
              ? 'Générez des QR codes pour vos équipements, suivez les signalements et analysez la qualité de service.'
              : `Consultation des réclamations pour votre site (${user?.localisation?.name || user?.localisation || 'Votre localisation'}).`
            }
          </p>
        </div>

        {/* Barre d'onglets */}
        <div className="qr-tab-nav">
          {isAdminOrUserAdmin && (
            <>
              <button
                className={`qr-tab-btn ${activeTab === 'qrcodes' ? 'active' : ''}`}
                onClick={() => setActiveTab('qrcodes')}
              >
                📡 QR Codes & Équipements
              </button>
              <button
                className={`qr-tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
                onClick={() => setActiveTab('categories')}
              >
                ⚙️ Motifs Personnalisés
              </button>
            </>
          )}
          <button
            className={`qr-tab-btn ${activeTab === 'reclamations' ? 'active' : ''}`}
            onClick={() => setActiveTab('reclamations')}
          >
            ⚠️ Signalements & Pannes
            {pendingCount > 0 && <span className="qr-tab-badge">{pendingCount}</span>}
          </button>
          <button
            className={`qr-tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            📊 Statistiques & Alertes
          </button>
        </div>
      </div>

      {/* ================= ONGLET 1: GESTION DES QR CODES ================= */}
      {isAdminOrUserAdmin && activeTab === 'qrcodes' && (
        <div>
          {/* Formulaires d'ajout */}
          <div className="row g-2 mb-2">
            {/* Formulaire 1: Créer un Équipement QR */}
            <div className="col-lg-8">
              <div className="qr-card h-100">
                <div className="qr-card-header">
                  <span>➕ Ajouter un Équipement avec QR Code</span>
                </div>
                <div className="qr-card-body">
                  <form onSubmit={handleCreateEquipment} className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label fw-bold text-dark">Localisation / Site *</label>
                      <select
                        className="form-select"
                        value={selectedLocationForEquip}
                        onChange={(e) => setSelectedLocationForEquip(e.target.value)}
                        required
                      >
                        <option value="">-- Choisir le site --</option>
                        {locations.map(loc => (
                          <option key={loc._id} value={loc._id}>{loc.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label fw-bold text-dark">Catégorie</label>
                      <select
                        className="form-select"
                        value={newEquipCategory}
                        onChange={(e) => setNewEquipCategory(e.target.value)}
                      >
                        <option value="Chaise de massage">🛋️ Chaise de massage</option>
                        <option value="Jeu VR">🎮 Jeu VR</option>
                        <option value="Simulator">🏎️ Simulateur</option>
                        <option value="Voiturette">🚗 Voiturette</option>
                        <option value="Table / Espace">🪑 Table / Espace</option>
                        <option value="Autre">✨ Autre équipement</option>
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label fw-bold text-dark">Nom de l'équipement *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ex: Chaise N°1, VR Room 2"
                        value={newEquipName}
                        onChange={(e) => setNewEquipName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="col-12 text-end mt-3">
                      <button type="submit" className="btn btn-primary px-4 fw-bold" disabled={submittingEquip}>
                        {submittingEquip ? 'Génération...' : '⚡ Générer le QR Code & Enregistrer'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Formulaire 2: Créer une Localisation Rapide */}
            <div className="col-lg-4">
              <div className="qr-card h-100">
                <div className="qr-card-header">
                  <span>📍 Nouvelle Localisation</span>
                </div>
                <div className="qr-card-body">
                  <form onSubmit={handleCreateLocation}>
                    <div className="mb-3">
                      <label className="form-label fw-bold text-dark">Nom du Site / Espace</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ex: Spintoss Lac 2, Twinpark"
                        value={newLocationName}
                        onChange={(e) => setNewLocationName(e.target.value)}
                        required
                      />
                    </div>
                    <button type="submit" className="btn btn-outline-primary w-100 fw-bold" disabled={submittingLoc}>
                      {submittingLoc ? 'Création...' : '➕ Ajouter le site'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Liste des équipements QR existants */}
          <div className="qr-card">
            <div className="qr-card-header d-flex justify-content-between align-items-center">
              <span>📋 Liste des Équipements Actifs ({equipments.length})</span>
              <button className="btn btn-sm btn-outline-secondary" onClick={fetchEquipments}>
                🔄 Actualiser
              </button>
            </div>
            <div className="qr-card-body p-0">
              {equipments.length === 0 ? (
                <div className="text-center p-5 text-muted">
                  <p className="fs-1 mb-2">📡</p>
                  <h5>Aucun équipement QR enregistré</h5>
                  <p className="small">Ajoutez votre premier équipement via le formulaire ci-dessus.</p>
                </div>
              ) : (
                <div className="qr-table-container">
                  <table className="qr-table">
                    <thead>
                      <tr>
                        <th>Équipement</th>
                        <th>Localisation</th>
                        <th>Catégorie</th>
                        <th>Date de Création</th>
                        <th className="text-center">Affiche QR / Partage</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {equipments.map(eq => (
                        <tr key={eq._id}>
                          <td>
                            <strong className="text-dark fs-6">🛋️ {eq.name}</strong>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border fs-6">
                              📍 {eq.location?.name || 'Non définie'}
                            </span>
                          </td>
                          <td>
                            <span className="text-muted fw-semibold">{eq.category}</span>
                          </td>
                          <td>
                            <span className="text-muted small">
                              {new Date(eq.createdAt).toLocaleDateString('fr-FR')}
                            </span>
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-success fw-bold px-2 py-1 me-1"
                              onClick={() => handleDownloadOnlyQRCode(eq)}
                              title="Télécharger UNIQUEMENT l'image du QR Code (PNG HD) pour l'exploiter directement"
                            >
                              ⬇️ Télécharger QR
                            </button>
                            <button
                              className="btn btn-sm btn-primary fw-bold px-2 py-1 me-1"
                              onClick={() => handleOpenPoster(eq)}
                              title="Générer l'affiche QR imprimable"
                            >
                              🖨️ Affiche
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary fw-semibold py-1"
                              onClick={() => handleCopyUrl(eq.qrToken)}
                              title="Copier le lien public"
                            >
                              📋 Lien
                            </button>
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteEquipment(eq._id)}
                              title="Supprimer cet équipement"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ================= ONGLET 2: MOTIFS PERSONNALISÉS PAR LOCALISATION ================= */}
      {isAdminOrUserAdmin && activeTab === 'categories' && (
        <div className="row g-2">
          <div className="col-lg-5">
            <div className="qr-card">
              <div className="qr-card-header">
                <span>➕ Ajouter un Motif Personnalisé</span>
              </div>
              <div className="qr-card-body">
                <form onSubmit={handleCreateCategory}>
                  <div className="mb-3">
                    <label className="form-label fw-bold text-dark">Sélectionner la Localisation</label>
                    <select
                      className="form-select"
                      value={selectedLocForCat}
                      onChange={(e) => setSelectedLocForCat(e.target.value)}
                      required
                    >
                      {locations.map(loc => (
                        <option key={loc._id} value={loc._id}>📍 {loc.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold text-dark">Type de Motif</label>
                    <select
                      className="form-select"
                      value={newCatType}
                      onChange={(e) => setNewCatType(e.target.value)}
                    >
                      <option value="reclamation">⚠️ Motif de Panne / Réclamation</option>
                      <option value="suggestion">💡 Type de Suggestion</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold text-dark">Intitulé du Motif</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ex: Monnayeur bloqué, Son coupé..."
                      value={newCatName}
                      onChange={(e) => setNewCatName(e.target.value)}
                      required
                    />
                  </div>

                  <button type="submit" className="btn btn-primary w-100 fw-bold" disabled={submittingCat}>
                    {submittingCat ? 'Enregistrement...' : '➕ Enregistrer le motif'}
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-7">
            <div className="qr-card">
              <div className="qr-card-header d-flex justify-content-between align-items-center">
                <span>📑 Motifs Disponibles pour le Site Sélectionné</span>
                {loadingCats && <div className="spinner-border spinner-border-sm text-primary"></div>}
              </div>
              <div className="qr-card-body p-0">
                {locationCategories.length === 0 ? (
                  <div className="text-center p-5 text-muted">
                    <p className="small">Aucun motif personnalisé pour ce site. Les motifs par défaut s'appliquent.</p>
                  </div>
                ) : (
                  <div className="qr-table-container">
                    <table className="qr-table">
                      <thead>
                        <tr>
                          <th>Motif</th>
                          <th>Type</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locationCategories.map(cat => (
                          <tr key={cat._id}>
                            <td className="fw-bold text-dark">{cat.name}</td>
                            <td>
                              {cat.type === 'reclamation' ? (
                                <span className="badge bg-danger-subtle text-danger border border-danger">
                                  ⚠️ Panne
                                </span>
                              ) : (
                                <span className="badge bg-primary-subtle text-primary border border-primary">
                                  💡 Suggestion
                                </span>
                              )}
                            </td>
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteCategory(cat._id)}
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= ONGLET 3: TABLEAU DES SIGNALEMENTS & PANNES ================= */}
      {activeTab === 'reclamations' && (
        <div>
          {/* Barre de Filtres */}
          <div className="qr-filter-bar">
            <div className="row g-3 align-items-center">
              {isAdminOrUserAdmin && (
                <div className="col-md-3">
                  <label className="form-label small fw-bold text-muted mb-1">Localisation / Site</label>
                  <select
                    className="form-select fw-semibold"
                    value={selectedLocationFilter}
                    onChange={(e) => setSelectedLocationFilter(e.target.value)}
                  >
                    <option value="">-- Tous les sites --</option>
                    {locations.map(loc => (
                      <option key={loc._id} value={loc._id}>📍 {loc.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className={isAdminOrUserAdmin ? "col-md-3" : "col-md-4"}>
                <label className="form-label small fw-bold text-muted mb-1">Statut d'Intervention</label>
                <select
                  className="form-select fw-semibold"
                  value={selectedStatusFilter}
                  onChange={(e) => setSelectedStatusFilter(e.target.value)}
                >
                  <option value="">-- Tous les statuts --</option>
                  <option value="en_attente">⏳ En attente</option>
                  <option value="en_cours">⚙️ En cours d'intervention</option>
                  <option value="resolu">✅ Résolu</option>
                </select>
              </div>

              <div className={isAdminOrUserAdmin ? "col-md-3" : "col-md-4"}>
                <label className="form-label small fw-bold text-muted mb-1">Type de Signalement</label>
                <select
                  className="form-select fw-semibold"
                  value={selectedTypeFilter}
                  onChange={(e) => setSelectedTypeFilter(e.target.value)}
                >
                  <option value="">-- Tous les types --</option>
                  <option value="reclamation">⚠️ Réclamations / Pannes</option>
                  <option value="suggestion">💡 Suggestions</option>
                </select>
              </div>

              <div className={isAdminOrUserAdmin ? "col-md-3 text-end pt-3" : "col-md-4 text-end pt-3"}>
                <button className="btn btn-primary fw-bold" onClick={fetchReclamations}>
                  🔄 Actualiser ({filteredReclamations.length})
                </button>
              </div>
            </div>
          </div>

          <div className="qr-card-body p-0">
            {filteredReclamations.length === 0 ? (
              <div className="text-center p-5 text-muted">
                <p className="fs-1 mb-2">🎉</p>
                <h5 className="fw-bold">Aucun signalement trouvé</h5>
                <p className="small">Aucune réclamation ne correspond à vos filtres actuels.</p>
              </div>
            ) : (
              <div className="qr-table-container">
                <table className="qr-table">
                  <thead>
                    <tr>
                      <th>Date / Heure</th>
                      <th>Type & Motif</th>
                      <th>Localisation & Équipement</th>
                      <th>Photo & Vidéo Client</th>
                      <th>Commentaire / Message</th>
                      <th>Statut</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReclamations.map(rec => (
                      <tr key={rec._id} className={rec.status === 'en_attente' ? 'row-pending' : ''}>
                        <td>
                          <strong className="d-block text-dark">
                            {new Date(rec.createdAt).toLocaleDateString('fr-FR')}
                          </strong>
                          <span className="text-muted small">
                            {new Date(rec.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>

                        <td>
                          {rec.type === 'reclamation' ? (
                            <span className="badge bg-danger mb-1 px-2 py-1">⚠️ Panne</span>
                          ) : (
                            <span className="badge bg-primary mb-1 px-2 py-1">💡 Suggestion</span>
                          )}
                          <div className="fw-bold text-dark fs-6">{rec.category}</div>
                        </td>

                        <td>
                          <div className="fw-bold text-primary">📍 {rec.location?.name || 'Inconnue'}</div>
                          {rec.equipment && (
                            <div className="text-dark small fw-semibold">🛋️ {rec.equipment.name}</div>
                          )}
                        </td>

                        <td>
                          <div className="qr-media-cell">
                            {/* Photo vignette */}
                            {rec.photoUrl && (
                              <div className="qr-photo-thumb-wrapper">
                                <img
                                  src={rec.photoUrl}
                                  alt="Photo signalement"
                                  className="qr-photo-thumb"
                                  onClick={() => setSelectedPhoto(rec.photoUrl)}
                                  title="Cliquer pour agrandir la photo"
                                />
                                <span className="qr-photo-zoom-icon">🔍</span>
                              </div>
                            )}

                            {/* Bouton Vidéo */}
                            {rec.videoUrl && (
                              <button
                                type="button"
                                className="qr-video-btn"
                                onClick={() => setSelectedVideo({
                                  url: rec.videoUrl,
                                  id: rec._id,
                                  clientName: rec.clientName,
                                  equipmentName: rec.equipment?.name,
                                  locationName: rec.location?.name,
                                  duration: rec.videoDuration,
                                  size: rec.videoSize
                                })}
                                title="Regarder la séquence vidéo enregistrée par le client"
                              >
                                🎬 Vidéo {rec.videoDuration ? `(${rec.videoDuration}s)` : ''}
                              </button>
                            )}

                            {!rec.photoUrl && !rec.videoUrl && (
                              <span className="text-muted small">Aucun média</span>
                            )}
                          </div>
                        </td>

                        <td style={{ maxWidth: '280px' }}>
                          <p className="small mb-1 text-wrap text-dark font-weight-medium">
                            {rec.description || <em>Aucun commentaire saisi</em>}
                          </p>
                          {(rec.clientName || rec.clientPhone) && (
                            <span className="badge bg-light text-dark border font-monospace">
                              👤 {rec.clientName} {rec.clientPhone ? `(${rec.clientPhone})` : ''}
                            </span>
                          )}
                        </td>

                        <td>
                          {rec.status === 'en_attente' && <span className="badge bg-warning text-dark fs-6 px-3 py-2">⏳ En attente</span>}
                          {rec.status === 'en_cours' && <span className="badge bg-info text-dark fs-6 px-3 py-2">⚙️ En cours</span>}
                          {rec.status === 'resolu' && <span className="badge bg-success fs-6 px-3 py-2">✅ Résolu</span>}
                          {rec.status === 'rejete' && <span className="badge bg-secondary fs-6 px-3 py-2">❌ Rejeté</span>}
                        </td>

                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            {rec.status !== 'resolu' && (
                              <button
                                className="btn btn-success fw-bold me-1"
                                onClick={() => handleStatusChange(rec._id, 'resolu')}
                                title="Marquer cette panne comme Résolue"
                              >
                                ✅ Résolu
                              </button>
                            )}
                            {rec.status === 'en_attente' && (
                              <button
                                className="btn btn-info fw-bold text-dark me-1"
                                onClick={() => handleStatusChange(rec._id, 'en_cours')}
                              >
                                ⚙️ En cours
                              </button>
                            )}
                            {isAdmin && (
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleDeleteReclamation(rec._id)}
                                title="Supprimer définitivement la réclamation (Admin uniquement)"
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================= ONGLET 4: STATISTIQUES DES PANNES ================= */}
      {activeTab === 'stats' && (
        <div>
          {/* Cartes KPI */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="qr-kpi-card blue">
                <div className="qr-kpi-label">Total Signalements</div>
                <div className="qr-kpi-value">{stats?.kpis?.total || 0}</div>
                <div className="qr-kpi-sub">
                  <strong>{stats?.kpis?.reclamations || 0}</strong> pannes / <strong>{stats?.kpis?.suggestions || 0}</strong> suggestions
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="qr-kpi-card yellow">
                <div className="qr-kpi-label">Pannes En Attente</div>
                <div className="qr-kpi-value text-warning">{stats?.kpis?.pending || 0}</div>
                <div className="qr-kpi-sub">Interventions non démarrées</div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="qr-kpi-card cyan">
                <div className="qr-kpi-label">En Cours d'Intervention</div>
                <div className="qr-kpi-value text-info">{stats?.kpis?.inProgress || 0}</div>
                <div className="qr-kpi-sub">Prises en charge par les techniciens</div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="qr-kpi-card green">
                <div className="qr-kpi-label">Taux de Résolution</div>
                <div className="qr-kpi-value text-success">{stats?.kpis?.resolutionRate || 0}%</div>
                <div className="qr-kpi-sub">
                  <strong>{stats?.kpis?.resolved || 0}</strong> signalements résolus
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4">
            {/* Répartition par Motif */}
            <div className="col-md-6">
              <div className="qr-card h-100 mb-0">
                <div className="qr-card-header">
                  <span>📊 Répartition par Motif de Panne</span>
                </div>
                <div className="qr-card-body">
                  {!stats?.byCategory || stats.byCategory.length === 0 ? (
                    <div className="text-center py-5 text-muted">Aucun signalement enregistré.</div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {stats.byCategory.map((cat, idx) => {
                        const total = stats.kpis?.total || 1;
                        const pct = Math.round((cat.count / total) * 100);
                        const colors = ['#ef4444', '#f59e0b', '#2563eb', '#10b981', '#8b5cf6', '#ec4899'];
                        const color = colors[idx % colors.length];
                        return (
                          <div key={idx}>
                            <div className="d-flex justify-content-between mb-1">
                              <span className="fw-bold text-dark">{cat._id}</span>
                              <span className="fw-bold text-dark">{cat.count} ({pct}%)</span>
                            </div>
                            <div className="progress" style={{ height: '10px', borderRadius: '6px', background: '#e2e8f0' }}>
                              <div
                                className="progress-bar"
                                style={{ width: `${pct}%`, backgroundColor: color, borderRadius: '6px' }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Top Équipements touchés */}
            <div className="col-md-6">
              <div className="qr-card h-100 mb-0">
                <div className="qr-card-header">
                  <span>🛋️ Top des Équipements Fréquemment en Panne</span>
                </div>
                <div className="qr-card-body">
                  {!stats?.byEquipment || stats.byEquipment.length === 0 ? (
                    <div className="text-center py-5 text-muted">Aucun équipement en panne.</div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {stats.byEquipment.map((eq, idx) => {
                        const maxCount = Math.max(...stats.byEquipment.map(e => e.count), 1);
                        const pct = Math.round((eq.count / maxCount) * 100);
                        return (
                          <div key={idx}>
                            <div className="d-flex justify-content-between mb-1">
                              <span className="fw-bold text-dark">🛋️ {eq.name} <small className="text-muted">({eq.locationName})</small></span>
                              <span className="badge bg-danger fs-6">{eq.count} pannes</span>
                            </div>
                            <div className="progress" style={{ height: '10px', borderRadius: '6px', background: '#e2e8f0' }}>
                              <div
                                className="progress-bar bg-danger"
                                style={{ width: `${pct}%`, borderRadius: '6px' }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Signalements par Localisation */}
            {isAdminOrUserAdmin && (
              <div className="col-md-12">
                <div className="qr-card mb-0">
                  <div className="qr-card-header">
                    <span>📍 Signalements par Localisation / Site</span>
                  </div>
                  <div className="qr-card-body">
                    {!stats?.byLocation || stats.byLocation.length === 0 ? (
                      <div className="text-center py-4 text-muted">Aucune donnée par site.</div>
                    ) : (
                      <div className="row g-3">
                        {stats.byLocation.map((loc, idx) => (
                          <div className="col-md-4" key={idx}>
                            <div className="p-3 border rounded-3 bg-light text-center">
                              <div className="fs-5 fw-bold text-primary">📍 {loc.name}</div>
                              <div className="fs-2 fw-bold text-dark mt-1">{loc.count}</div>
                              <small className="text-muted">signalements enregistrés</small>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================= MODAL LECTEUR VIDÉO CLIENT (AVEC SUPPRESSION ADMIN DÉFINITIVE) ================= */}
      {selectedVideo && (
        <div className="modal fade show d-block qr-modal-backdrop" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg bg-dark text-white">
              <div className="modal-header border-secondary">
                <h5 className="modal-title fw-bold text-info">
                  🎥 Séquence Vidéo du Client
                  {selectedVideo.equipmentName ? ` • ${selectedVideo.equipmentName}` : ''}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setSelectedVideo(null)}></button>
              </div>

              <div className="modal-body text-center p-3">
                <video
                  src={selectedVideo.url}
                  controls
                  autoPlay
                  playsInline
                  className="qr-video-modal-player"
                />

                <div className="d-flex justify-content-between align-items-center mt-3 px-2 text-start text-muted small">
                  <div>
                    📍 <strong className="text-white">{selectedVideo.locationName || 'Site'}</strong>
                    {selectedVideo.clientName && <span> • 👤 {selectedVideo.clientName}</span>}
                  </div>
                  <div>
                    {selectedVideo.size > 0 && (
                      <span className="badge bg-secondary me-2">
                        💾 {(selectedVideo.size / 1024).toFixed(0)} Ko
                      </span>
                    )}
                    {selectedVideo.duration > 0 && (
                      <span className="badge bg-info text-dark">
                        ⏱️ {selectedVideo.duration}s
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer border-secondary d-flex justify-content-between">
                <div>
                  {isAdmin && (
                    <button
                      type="button"
                      className="btn btn-danger fw-bold"
                      onClick={() => handleDeleteVideo(selectedVideo.id)}
                      disabled={deletingVideoId === selectedVideo.id}
                      title="Supprimer définitivement ce fichier vidéo du serveur et de la base de données"
                    >
                      {deletingVideoId === selectedVideo.id ? 'Suppression...' : '🗑️ Supprimer Définitivement la Vidéo'}
                    </button>
                  )}
                </div>
                <button type="button" className="btn btn-secondary fw-bold" onClick={() => setSelectedVideo(null)}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL POSTER QR CODE IMPRIMABLE & PARTAGE EXTERNE ================= */}
      {posterEquip && (
        <div className="modal fade show d-block qr-modal-backdrop" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header bg-dark text-white">
                <h5 className="modal-title fw-bold">🖨️ Affiche QR & Lien Externe Client</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setPosterEquip(null)}></button>
              </div>

              <div className="modal-body text-center p-4">
                <div className="qr-poster-box" id="printable-qr-poster">
                  <div className="qr-poster-header">
                    <span className="qr-poster-badge">✨ SERVICE CLIENT & ASSISTANCE</span>
                    <h3>📍 {posterEquip.location?.name || 'NOTRE ESPACE'}</h3>
                    <h4>🛋️ {posterEquip.name}</h4>
                  </div>
                  
                  <div
                    className="qr-poster-qr-frame my-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleDownloadOnlyQRCode(posterEquip)}
                    title="Cliquer pour télécharger directement l'image du QR Code (PNG HD)"
                  >
                    <img src={posterQrUrl} alt="QR Code Client Externe" className="qr-poster-img" />
                    <div className="qr-scan-badge">⬇️ Cliquer pour télécharger l'image QR</div>
                  </div>

                  <div className="qr-poster-instructions">
                    <p className="fw-bold fs-5 text-primary mb-1">Une panne ou une suggestion ?</p>
                    <p className="text-muted small">
                      Scannez ce QR Code avec l'appareil photo de votre smartphone pour accéder directement au formulaire de signalement avec photo et vidéo.
                    </p>
                  </div>

                  <div className="qr-poster-footer mt-3">
                    <small className="text-muted font-monospace d-block text-break">
                      🌐 {getClientFormUrl(posterEquip.qrToken)}
                    </small>
                  </div>
                </div>

                <div className="qr-external-share-box mt-4 p-3 bg-light rounded-3 border">
                  <h6 className="fw-bold text-dark mb-2">🔗 Exploiter & Partager :</h6>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <button
                      className="btn btn-success btn-sm fw-bold"
                      onClick={() => handleDownloadOnlyQRCode(posterEquip)}
                      title="Télécharger uniquement le QR code en PNG haute qualité pour l'intégrer dans vos supports"
                    >
                      ⬇️ Télécharger QR Code seul (PNG HD)
                    </button>

                    <button
                      className="btn btn-outline-success btn-sm fw-bold"
                      onClick={() => {
                        const url = getClientFormUrl(posterEquip.qrToken);
                        const waText = encodeURIComponent(`Bonjour, voici le lien direct pour effectuer vos réclamations & suggestions pour ${posterEquip.name} (${posterEquip.location?.name}) :\n${url}`);
                        window.open(`https://wa.me/?text=${waText}`, '_blank');
                      }}
                    >
                      💬 Partager sur WhatsApp
                    </button>

                    <button
                      className="btn btn-outline-primary btn-sm fw-bold"
                      onClick={() => {
                        window.open(getClientFormUrl(posterEquip.qrToken), '_blank');
                      }}
                    >
                      🌐 Ouvrir Vue Client
                    </button>

                    <button
                      className="btn btn-outline-secondary btn-sm fw-bold"
                      onClick={() => handleCopyUrl(posterEquip.qrToken)}
                    >
                      📋 Copier Lien
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-footer bg-light d-flex justify-content-between flex-wrap gap-2">
                <button type="button" className="btn btn-secondary fw-bold" onClick={() => setPosterEquip(null)}>
                  Fermer
                </button>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-success fw-bold"
                    onClick={() => handleDownloadOnlyQRCode(posterEquip)}
                    title="Télécharge l'image brute du QR code en haute définition"
                  >
                    ⬇️ Télécharger QR Code seul (PNG)
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary fw-bold"
                    onClick={handlePrintPoster}
                  >
                    🖨️ Imprimer l'Affiche Complète
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL AGRANDISSEMENT PHOTO CLIENT ================= */}
      {selectedPhoto && (
        <div className="modal fade show d-block qr-modal-backdrop" tabIndex="-1" onClick={() => setSelectedPhoto(null)}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content bg-transparent border-0 text-center">
              <img src={selectedPhoto} alt="Photo signalement grand format" className="img-fluid rounded-4 shadow-lg border border-3 border-white" style={{ maxHeight: '85vh', objectFit: 'contain' }} />
              <div className="mt-3 text-white fw-bold bg-dark bg-opacity-75 d-inline-block mx-auto px-4 py-2 rounded-pill">
                🔍 Cliquer n'importe où pour fermer
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRManagement;
